import {
  FanEngagementFragment,
  useCanCreateSubmissionQuery,
} from '@/generated/graphql';
import { NewPhotoForm } from './NewPhotoForm';
import {
  PHOTO_SHARE_LIMIT_HEADLINE,
  PHOTO_SHARE_LIMIT_THANKS,
} from './limitCopy';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FullScreenLoader } from '@/components/Loader';
import { useDeviceAccess } from '@/hooks/useDeviceAccess';
import { useDeviceSignalVariables } from '@/hooks/useDeviceIdentity';

function PhotoShareFanStatus({
  emoji,
  headline,
  detail,
}: {
  emoji: string;
  headline: string;
  detail?: string;
}) {
  return (
    <div
      className={`flex h-screen flex-col items-center justify-center space-y-4 p-8 text-center`}
    >
      <div className="text-6xl">{emoji}</div>
      <h3 className="font-screen-body text-2xl text-screen-foreground">
        {headline}
      </h3>
      {detail ? (
        <p className="font-screen-body text-lg text-screen-foreground">
          {detail}
        </p>
      ) : null}
    </div>
  );
}

export function PhotoCarouselFanView({
  engagement,
}: {
  engagement: FanEngagementFragment;
}) {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { variables: deviceSignals, ready } = useDeviceSignalVariables();
  const deviceAccess = useDeviceAccess(engagement.id);
  const { data, loading, refetch } = useCanCreateSubmissionQuery({
    skip: !engagement.id || !ready,
    variables: { engagementId: engagement.id, ...deviceSignals },
    fetchPolicy: 'network-only',
  });
  const handleSuccess = useCallback(() => {
    setHasSubmitted(true);
    void refetch();
  }, [refetch]);

  const canCreateSubmission = data?.canCreateSubmission === true;

  if (!deviceAccess.allowed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center space-y-4 p-8 text-center">
        <h3 className="font-screen-body text-2xl text-screen-foreground">
          {deviceAccess.message}
        </h3>
      </div>
    );
  }

  if (hasSubmitted) {
    if (canCreateSubmission) {
      return (
        <div
          className={`flex h-screen flex-col items-center justify-center space-y-4 p-8 text-center`}
        >
          <div className="text-6xl">🤳</div>
          <h3 className="font-screen-body text-2xl text-screen-foreground">
            You should see your photo on the screen soon!
          </h3>
          <Button
            className="font-screen-body text-2xl"
            variant="default"
            onClick={() => setHasSubmitted(false)}
          >
            take another
          </Button>
        </div>
      );
    }

    return (
      <PhotoShareFanStatus
        emoji="🤳"
        headline="You should see your photo on the screen soon!"
        detail={PHOTO_SHARE_LIMIT_THANKS}
      />
    );
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  if (data && !canCreateSubmission) {
    return (
      <PhotoShareFanStatus
        emoji="📸"
        headline={PHOTO_SHARE_LIMIT_HEADLINE}
        detail={PHOTO_SHARE_LIMIT_THANKS}
      />
    );
  }

  return <NewPhotoForm engagement={engagement} onSuccess={handleSuccess} />;
}
